import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import CloseIcon from '@mui/icons-material/Close';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import NaturePeopleIcon from '@mui/icons-material/NaturePeople';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import {
  Alert,
  AlertTitle,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { DatePicker } from 'antd';
import classNames from 'classnames';
import {
  addDays,
  differenceInDays,
  endOfDay,
  endOfMonth,
  format,
  getUnixTime,
  isFuture,
  isToday,
  startOfDay,
  startOfMonth,
  subDays,
} from 'date-fns';
import dayjs from 'dayjs';
import moment from 'moment-timezone';
import * as React from 'react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import useSWR from 'swr';

import { IInvoices, ILineItems } from '@/Billing/types';
import { useBills } from '@/Billing/useBills';
import { DeviceStatusTable } from '@/components/DeviceStatusTable';
import { DeleteConfirmDialog } from '@/components/Dialog';
import { SWLoadingButton } from '@/components/LoadingButton';
import { SWLoadingButtonOutlined } from '@/components/SWLoadingButtonOutlined';
import useMixPanel from '@/hooks/useMixPanel';
import { usePersistedDateRange } from '@/hooks/usePersistedDateRange';
import { usePersistedUsageGranularity } from '@/hooks/usePersistedUsageGranularity';
import { Permission, useUserPermissions } from '@/hooks/useUserPermissions';
import { defaultOutageStatusResponse, getOutageStatus } from '@/Outage/api';
import {
  mapOutageStatusToIsElasticsearchOutage,
  OUTAGE_STATUS_REFRESH_MILLIS,
  OUTAGE_STATUS_SWR_KEY,
} from '@/Outage/util';
import { getUserInfo } from '@/pages/AccountSettings/api';
import { AlertsView } from '@/Structures/alerts';
import { useSubscribers } from '@/Structures/hooks/useSubscribers';
import { convertTimestampToTimeZone } from '@/util';

import { createStructureRoute } from '../../app-routes';
import { Chart } from '../../components/Chart';
import {
  buildEstimatedChartData,
  createLabel as createChartLabel,
} from '../../components/Chart/util';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { UsageTotalHeadline } from '../../components/UsageTotalHeadline';
import { useStructure } from '../../Structures/useStructures';
import theme from '../../theme';
import type { Address as AddressType } from '../../types';
import { get as getUsage, getHourly as getHourlyUsage } from '../../Usage/api';
import { getTotalVolume } from '../../Usage/util';
import { addCreate, naturalCompare } from '../../util';
import { edit, remove } from '../api';
import { BillHistoryTable } from '../billHistory';
import { UsageGraphMode } from '../types';
import { useUnit } from '../useUnit';
import { generateHourlyUsageCSV, generateUsageCSV } from '../util';
import { Address } from './Address';
import { Name } from './Name';
const { RangePicker } = DatePicker;

const dateFormat = 'YYYY-MM-DD';

// Component //////////////////////////////////////////////////////////////////

const useStyles = makeStyles((theme) => ({
  wrapper: {
    display: 'flex',
    width: '100%',
  },
  container: {
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
  },
  header: {
    alignItems: 'baseline',
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(3),
    paddingBottom: 0,
    paddingLeft: 0,
    textAlign: 'left',
  },
  chartBody: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    // position: relative so the chartLoader (which uses position: absolute +
    // top: 50%) is anchored to the chart area itself instead of bubbling up to
    // the outer chart-section wrapper. Without this, on multi-meter unit pages
    // (which add a "Combined Usage" header above the chart and a Per Meter
    // Breakdown chart below it) the loader's 50% point slid down past the
    // actual chart's middle.
    position: 'relative',
  },
  chartLoader: {
    // Chart has a fixed width of 700px (see components/Chart/index.tsx, VictoryContainer
    // responsive=false). Anchor the loader to that, not to chartBody's variable width,
    // so the bar's length is consistent across all account contexts and view layouts.
    // 490px is ~70% of the chart width — visible without overshooting either axis.
    width: 490,
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    position: 'absolute',
  },
  editIcon: {
    marginLeft: theme.spacing(1),
    marginTop: theme.spacing(-0.5),
    opacity: 0.3,
    '&:hover': {
      opacity: 1,
    },
  },
  prevNextButton: {
    height: 'fit-content',
  },
  nextButton: {
    marginLeft: theme.spacing(1),
  },
  prevButton: {
    marginRight: theme.spacing(1),
  },
}));

const today = new Date();
export interface IUnitBill {
  invoices: IInvoices[];
  cost: number;
  costUnits: 'DOLLARS';
  endDate: number;
  id: string;
  startDate: number;
  structureId: string;
  volume: number;
  volumeUnits: 'GALLONS';
  name?: string;
  rateMethod?: 'SIMPLESUB' | 'UTILITY' | 'CUSTOM';
  effectiveRate?: number | null;
  billsSentTimestamp?: number | null;
  lineItems: ILineItems[];
}

/**
 * The four bill-model states a unit can be in. Backed by combinations of
 * `unit_flags`:
 *   BY_USAGE                    → []
 *   NON_BILLABLE_UNIT           → ['NON_BILLABLE']
 *   SPLIT                       → ['COMMON_AREA', 'SPLIT']
 *   NON_BILLABLE_COMMON_AREA    → ['COMMON_AREA', 'NON_BILLABLE']
 *
 * The DB enum allows other combinations, but the product UI only ever produces
 * these four. The two NON_BILLABLE variants need to stay distinct because
 * "non-billable regular unit" and "non-billable common area" are different
 * things to the user (the former keeps tenants and an address; the latter is
 * shared infrastructure).
 */
type BillModel = 'BY_USAGE' | 'NON_BILLABLE_UNIT' | 'SPLIT' | 'NON_BILLABLE_COMMON_AREA';

const billModelFromFlags = (flags: ReadonlyArray<string>): BillModel => {
  const isCommonArea = flags.includes('COMMON_AREA');
  const isNonBillable = flags.includes('NON_BILLABLE');
  const isSplit = flags.includes('SPLIT');
  if (isSplit) return 'SPLIT';
  if (isCommonArea && isNonBillable) return 'NON_BILLABLE_COMMON_AREA';
  if (isNonBillable) return 'NON_BILLABLE_UNIT';
  return 'BY_USAGE';
};

const flagsForBillModel = (model: BillModel): ('NON_BILLABLE' | 'COMMON_AREA' | 'SPLIT')[] => {
  switch (model) {
    case 'BY_USAGE':
      return [];
    case 'NON_BILLABLE_UNIT':
      return ['NON_BILLABLE'];
    case 'SPLIT':
      return ['COMMON_AREA', 'SPLIT'];
    case 'NON_BILLABLE_COMMON_AREA':
      return ['COMMON_AREA', 'NON_BILLABLE'];
  }
};

const BILL_MODEL_LABELS: Record<BillModel, string> = {
  BY_USAGE: 'By Usage',
  NON_BILLABLE_UNIT: 'Non Billable',
  SPLIT: 'Split',
  NON_BILLABLE_COMMON_AREA: 'Non Billable',
};

export const Unit = (): JSX.Element => {
  const [value, setValue] = useState('2');
  const navigate = useNavigate();
  const classes = useStyles();

  const [loading, setLoading] = React.useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = React.useState(false);
  const { range, setRange, moveByMonth } = usePersistedDateRange();
  const [canGoNext, setCanGoNext] = React.useState(false);
  const [timeZone, setTimeZone] = React.useState<string>('');
  const [invoices, setInvoices] = React.useState<IUnitBill[]>();
  const [dialogModel, setDialogModel] = React.useState(false);
  const [unitModel, setUnitModel] = React.useState<BillModel>();
  const [selectedModel, setSelectedModel] = React.useState<BillModel>();
  const [stripeConnect, setStripeConnect] = React.useState<boolean | undefined>(undefined);

  React.useEffect(() => {
    const getData = async () => {
      const userInfo = await getUserInfo();
      if (userInfo) {
        const stripeInfo =
          userInfo.user.stripe_connected_account_id !== '' &&
          userInfo.user.stripe_connected_account_details_submitted
            ? true
            : false;
        setStripeConnect(stripeInfo);
      }
    };
    if (stripeConnect === undefined) getData();
  }, [stripeConnect]);

  const { structureId, unitId } = useParams();

  if (!unitId || !structureId) {
    throw new Error('MISSING_REQUIRED_ROUTE_DATA');
  }

  /** Structure */
  const { structure } = useStructure(structureId);

  const { bills = [], isLoading } = useBills(structureId);

  // Tenants on this property, filtered to just the ones assigned to this unit.
  // Used by the Bill Model confirmation dialog to surface a soft warning when
  // the user is flipping a Unit (with tenants) to a Common Area (which by
  // convention doesn't carry tenants). The product still permits the
  // assignment, so we inform rather than block.
  const { subscribers, isLoading: subscribersLoading } = useSubscribers(structureId);
  // The backend returns subscribers in insertion order, which is not useful
  // when surfacing them in the Bill Model dialog. Sort by display name (the
  // same string we render) so the list is deterministic and scannable; fall
  // back to email when first/last are blank so empty-named tenants land in a
  // predictable spot.
  const tenantDisplayName = (s: { first_name?: string; last_name?: string; email: string }) => {
    const fullName = `${s.first_name ?? ''} ${s.last_name ?? ''}`.trim();
    return fullName || s.email;
  };
  const unitTenants = subscribers
    .filter((s) => s.unit_subscriptions.some((u) => u.unit_id === unitId))
    .sort((a, b) => naturalCompare(tenantDisplayName(a), tenantDisplayName(b)));

  React.useEffect(() => {
    const getInvoices = () => {
      const filteredBills = bills.map((bill) => ({
        ...bill, // Copiamos todas las propiedades del bill original
        invoices: bill.invoices.filter((invoice) => invoice.unitId === unitId), // Filtramos los invoices
      }));
      setInvoices(filteredBills);
    };
    if (!isLoading && bills) getInvoices();
  }, [bills, isLoading, unitId]);

  React.useEffect(() => {
    if (structure && timeZone === '') {
      if (structure.timezone_key !== '') setTimeZone(structure.timezone_key);
      if (structure.timezone_key === '') {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        setTimeZone(tz);
      }
    }
  }, [structure, timeZone]);

  /** Unit, call to refresh unit data */
  const { unit, mutate: refreshData } = useUnit(unitId);

  const { trackEvent } = useMixPanel();

  /** Shared payload for the unit usage view/export events; mirrors 'Property Usage Viewed'. */
  const eventData = React.useMemo(
    () =>
      unit && structure && range
        ? {
            PropertyID: structureId,
            PropertyName: structure.name,
            UnitID: unitId,
            UnitName: unit.name,
            StartDate: convertTimestampToTimeZone(getUnixTime(startOfDay(range[0]!)), undefined),
            EndDate: convertTimestampToTimeZone(getUnixTime(startOfDay(range[1]!)), undefined),
          }
        : undefined,
    [unit, structure, structureId, unitId, range],
  );

  React.useEffect(() => {
    if (eventData) trackEvent('Unit Usage Viewed', eventData);
    // trackEvent is recreated every render; only the payload should retrigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventData]);

  const { data: outageStatusResponse = defaultOutageStatusResponse } = useSWR(
    OUTAGE_STATUS_SWR_KEY,
    getOutageStatus,
    {
      refreshInterval: OUTAGE_STATUS_REFRESH_MILLIS,
    },
  );

  // Per-membership HOURLY_USAGE flag from /me/permissions. When true, the customer sees a
  // toggle that lets them switch between daily and hourly usage. The flag is granted by
  // Customer Support; customers without it never see the toggle and only get daily.
  // The granularity choice is persisted in sessionStorage (same pattern as the date range)
  // so it carries across navigation between units and properties.
  const { hasHourlyUsage, hasPermission, loading: permissionsLoading } = useUserPermissions();
  // Billing managers lack VIEW_USAGE. Gate the usage fetch and tab the same way the
  // structure-level views (BillingWithStripe / NonBilling) do, so they get a friendly
  // message instead of a failed request.
  const hasViewUsage = hasPermission(Permission.VIEW_USAGE);
  // Optimistic flag for UI only (tab + message): assume allowed while permissions load so
  // we don't flash a disabled state.
  const canViewUsage = permissionsLoading || hasViewUsage;
  // Strict flag for the actual fetch: only fire once permissions have RESOLVED and the user
  // has VIEW_USAGE. Gating the request on the optimistic flag would fire it during the
  // loading window and surface an error toast before we know the user lacks access.
  const shouldFetchUsage = !permissionsLoading && hasViewUsage;
  const { granularity: usageGranularity, setGranularity: setUsageGranularity } =
    usePersistedUsageGranularity();
  const showHourly = hasHourlyUsage && usageGranularity === 'hourly';

  // Multi-meter units get a SECOND, per-meter breakdown chart rendered below the
  // combined chart (no toggle; both views are always visible when relevant).
  // Single-meter units render only the combined chart, exactly as before.
  const hasMultipleMeters = (unit?.devices?.length ?? 0) > 1;

  // Load usage — branch on the granularity. The two SWR keys are distinct so cache
  // doesn't collide when the user toggles between modes.
  const { data: rawUsage, error: usageError } = useSWR(
    !shouldFetchUsage || showHourly
      ? null
      : `units/${unitId}/usage?start=${getUnixTime(startOfDay(range[0]!))}&end=${getUnixTime(
          endOfDay(range[1]!),
        )}`,
    () =>
      getUsage(
        unitId,
        range[0]!,
        range[1]!,
        mapOutageStatusToIsElasticsearchOutage(outageStatusResponse),
        timeZone,
      ),
    {
      shouldRetryOnError: false, // Don't retry on permission errors
    },
  );

  const { data: rawHourlyUsage, error: hourlyUsageError } = useSWR(
    shouldFetchUsage && showHourly
      ? `units/${unitId}/usage/hourly?start=${getUnixTime(startOfDay(range[0]!))}&end=${getUnixTime(
          endOfDay(range[1]!),
        )}`
      : null,
    () =>
      getHourlyUsage(
        unitId,
        range[0]!,
        range[1]!,
        mapOutageStatusToIsElasticsearchOutage(outageStatusResponse),
        timeZone,
      ),
    {
      shouldRetryOnError: false,
    },
  );

  // Check if error is permission denied (covers both daily and hourly fetch errors)
  const isPermissionDenied = (usageError ?? hourlyUsageError)?.message?.includes(
    'PERMISSION_DENIED',
  );

  // Show the friendly "no permission" message either proactively (role lacks
  // VIEW_USAGE, so we never fetched) or reactively (a fetch came back 403).
  const showUsagePermissionMessage = !canViewUsage || isPermissionDenied;

  // Per-meter series for multi-meter units in Per Meter mode. Built independently
  // from the combined graphData so the existing single-series render path is
  // untouched in the common case. Each device becomes one Series; the chart stacks
  // them on the shared x-axis. We always build this (even in Combined mode) so the
  // toggle flips instantly without re-fetching; it's just a render-mode switch.
  const meterSeries = React.useMemo(() => {
    if (!hasMultipleMeters) return [];

    const startStr = format(range[0]!, 'yyyy-MM-dd');
    const endStr = format(range[1]!, 'yyyy-MM-dd');

    if (showHourly) {
      return (
        rawHourlyUsage?.devices?.map((device) => ({
          name: device.name || device.id,
          data: device.hourly_usages.map((bucket) => {
            const epochMs = bucket.time * 1000;
            const tz = timeZone || moment.tz.guess();
            const labelTime = moment.tz(epochMs, tz).format('M/D/YYYY h:mm A');
            return {
              label: `${bucket.volume.toFixed(2)} gal at ${labelTime}`,
              gallons: bucket.volume,
              volume: bucket.volume,
              date: format(new Date(epochMs), 'yyyy-MM-dd'),
              xValue: epochMs,
            };
          }),
        })) ?? []
      );
    }

    return (
      rawUsage?.devices?.map((device) => ({
        name: device.name || device.id,
        data: device.daily_usages
          .filter((d) => d.date >= startStr && d.date <= endStr)
          .sort((a, b) => (a.date < b.date ? -1 : 1))
          .map((d) => ({
            label: createChartLabel(d.volume, d.date),
            gallons: d.volume,
            volume: d.volume,
            date: d.date,
          })),
      })) ?? []
    );
  }, [hasMultipleMeters, showHourly, rawUsage, rawHourlyUsage, range, timeZone]);

  // Compute graph data synchronously from the current state. This used to be
  // a useState + useEffect pair, which produced a one-frame window where the
  // Chart received the new `granularity` prop alongside the previous mode's
  // `data` (since the effect ran after render). Symptom in production was an
  // hourly-shaped data point rendered with daily x-axis labels, e.g. seven
  // "May 1st" ticks and a single bar whose tooltip showed an hourly time of
  // day. useMemo recomputes during render in the same pass that updated
  // showHourly, so granularity and data can never disagree.
  //
  // Returns [] when the relevant SWR fetch is still in flight or the unit has
  // no usage in range. The Chart's chartKey has an explicit "-empty" branch
  // for this case, which gives a clean remount when real data lands instead
  // of a 2-second animation morph.
  const graphData = React.useMemo(() => {
    if (showHourly) {
      const usageByHour: { [hourEpoch: string]: number } = {};
      rawHourlyUsage?.devices?.forEach((device) => {
        device.hourly_usages.forEach((bucket) => {
          addCreate(usageByHour, String(bucket.time), bucket.volume);
        });
      });
      return Object.entries(usageByHour)
        .map(([epochSecondStr, vol]) => {
          const epochMs = Number(epochSecondStr) * 1000;
          const dateStr = format(new Date(epochMs), 'yyyy-MM-dd');
          // Bake the hourly tooltip label directly into the datum so VictoryTooltip
          // renders it via datum.label (more reliable across Victory versions than the
          // labels-callback prop). Time is rendered in the property's timezone.
          const tz = timeZone || moment.tz.guess();
          const labelTime = moment.tz(epochMs, tz).format('M/D/YYYY h:mm A');
          return {
            label: `${(vol as number).toFixed(2)} gallons used at ${labelTime}`,
            gallons: vol as number,
            volume: vol as number,
            date: dateStr,
            xValue: epochMs,
          };
        })
        .sort((a, b) => a.xValue - b.xValue);
    }

    const usageByDate: { [dateStr: string]: number } = {};
    rawUsage?.devices?.forEach((device) => {
      device.daily_usages.forEach((usageData) => {
        addCreate(usageByDate, usageData.date, usageData.volume);
      });
    });

    const startStr = format(range[0]!, 'yyyy-MM-dd');
    const endStr = format(range[1]!, 'yyyy-MM-dd');

    return Object.entries(usageByDate)
      .map(([date, vol]) => ({
        label: createChartLabel(vol as number, date),
        gallons: vol as number,
        volume: vol as number,
        date, // 'YYYY-MM-DD'
      }))
      .filter((d) => d.date >= startStr && d.date <= endStr)
      .sort((a, b) => (a.date < b.date ? -1 : 1));
  }, [rawUsage, rawHourlyUsage, showHourly, range, timeZone]);

  // Estimated usage overlay data (support-entered estimates for offline-meter
  // periods). Aggregated per bucket with tooltip labels baked in; the Chart stacks
  // it above the real series per the W2 design. Volumes are gallons, same as the
  // real series, so no separate conversion happens here.
  const estimatedGraphData = React.useMemo(() => {
    const raw = showHourly ? rawHourlyUsage : rawUsage;
    return buildEstimatedChartData(
      raw?.estimated_bucket_volumes ?? [],
      showHourly ? 'hourly' : 'daily',
      timeZone || moment.tz.guess(),
    );
  }, [rawUsage, rawHourlyUsage, showHourly, timeZone]);

  // En/disable next button
  React.useEffect(() => {
    if (range[1]) {
      if (isFuture(range[1]) || isToday(range[1])) {
        setCanGoNext(false);
      } else {
        setCanGoNext(true);
      }
    }
  }, [range]);

  React.useEffect(() => {
    if (unit) {
      setUnitModel(billModelFromFlags(unit.unit_flags));
    }
  }, [unit]);

  const handleClickNext = () => {
    if (moveByMonth) {
      const newMonth = addDays(range[1]!, 3);
      setRange([startOfDay(startOfMonth(newMonth)), startOfDay(endOfMonth(newMonth))]);
    } else {
      const numberOfDaysInRange = differenceInDays(range[1]!, range[0]!);

      const newFrom = addDays(range[0]!, numberOfDaysInRange + 1);
      const newTo = addDays(range[1]!, numberOfDaysInRange + 1);

      setRange([startOfDay(newFrom), startOfDay(newTo)]);
    }
  };

  const handleClickPrev = () => {
    if (moveByMonth) {
      const newMonth = subDays(range[0]!, 3);
      setRange([startOfDay(startOfMonth(newMonth)), startOfDay(endOfMonth(newMonth))]);
    } else {
      const numberOfDaysInRange = differenceInDays(range[1]!, range[0]!);

      const newFrom = subDays(range[0]!, numberOfDaysInRange + 1);
      const newTo = subDays(range[1]!, numberOfDaysInRange + 1);
      setRange([startOfDay(newFrom), startOfDay(newTo)]);
    }
  };

  const handleDateChange = (dates) => {
    const startDate = new Date(dates[0]);
    const endDate = new Date(dates[1]);
    startDate?.setHours(0, 0, 0, 0);
    endDate?.setHours(23, 59, 59, 0);
    setRange([startDate, endDate]);
  };

  const handleClickDeleteUnit = () => {
    setOpenDeleteConfirm(true);
  };

  const handleClickRemove = async () => {
    if (!unit) return;

    setLoading(true);
    try {
      await remove(unit.id);
      navigate(createStructureRoute(structureId));
      refreshData();
      setOpenDeleteConfirm(false);
    } catch (err) {
      // Error already shown by API layer, keep dialog open
      console.error('Failed to delete unit:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUnit = (fieldName: string) => async (value: string | AddressType) => {
    if (!unit) return;
    setLoading(true);
    try {
      await edit({ ...unit, [fieldName]: value });
      refreshData();
    } finally {
      setLoading(false);
    }
  };

  const handleToggleIncludeUsageGraph = async (
    _e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    if (!unit) return;
    setLoading(true);
    try {
      await edit({
        ...unit,
        usage_graph_mode: checked ? UsageGraphMode.INCLUDED : UsageGraphMode.HIDDEN,
      });
      refreshData();
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUnitFlags = async () => {
    if (!unit || !selectedModel) return;
    setLoading(true);
    try {
      await edit({ ...unit, unit_flags: flagsForBillModel(selectedModel) });
      // Await the SWR revalidation so the dialog stays open (with the
      // Confirm button's spinner still visible) until the new bill model
      // is actually reflected in `unit`. Closing earlier flashed the old
      // dropdown value back in the page underneath while the refetch was
      // still in flight.
      await refreshData();
      setDialogModel(false);
    } catch (err) {
      // Error toast is already raised by the API layer; keep the dialog
      // open so the user can retry or cancel.
      // eslint-disable-next-line no-console
      console.error('Failed to update bill model:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  // True while the relevant SWR fetch is in flight (rawUsage / rawHourlyUsage is undefined
  // until the first response arrives). Drives the chart-overlay <LinearProgress />.
  const isLoadingUsage = showHourly ? !rawHourlyUsage : !rawUsage;
  return (
    <ErrorBoundary>
      <div data-component-id={'UNIT-VIEW'} className={classes.wrapper}>
        {unit && unitModel && (
          <div className={classes.container}>
            <div className={classes.header}>
              <div style={{ flexGrow: 1 }}>
                <Stack direction={'row'} spacing={0} justifyContent={'space-between'}>
                  <Box>
                    <Name
                      name={unit?.name || ''}
                      onUpdate={handleUpdateUnit('name')}
                      onDelete={handleClickDeleteUnit}
                    />

                    <Stack direction={'row'} spacing={0} alignItems={'center'}>
                      {unit.unit_flags.includes('COMMON_AREA') ? (
                        <Stack direction={'row'} gap={1}>
                          <NaturePeopleIcon htmlColor="black" />
                          <Typography fontWeight={'bold'}>Common Area</Typography>
                          <Divider
                            orientation="vertical"
                            flexItem
                            sx={{ marginLeft: 1, borderWidth: 1 }}
                          />
                        </Stack>
                      ) : (
                        <Stack direction={'row'} gap={1}>
                          <MeetingRoomIcon htmlColor="black" />
                          <Typography fontWeight={'bold'}>Unit</Typography>
                          <Divider
                            orientation="vertical"
                            flexItem
                            sx={{ marginLeft: 1, borderWidth: 1 }}
                          />
                        </Stack>
                      )}

                      <Box marginLeft={1}>
                        <Address
                          address={
                            unit?.address || {
                              street: '',
                              street2: '',
                              city: '',
                              state: '',
                              zip: '',
                            }
                          }
                          onUpdate={handleUpdateUnit('address')}
                          loading={loading}
                        />
                      </Box>
                    </Stack>
                  </Box>
                  <Stack direction="row" spacing={3} alignItems="flex-start">
                    <Stack>
                      <Tooltip title="When enabled, generated custom invoice PDFs for this unit include the daily usage bar chart. Turn off if usage data is incomplete or estimated.">
                        <Typography fontWeight={'bold'} fontSize={15}>
                          Usage Graph on Invoices
                        </Typography>
                      </Tooltip>
                      <FormControlLabel
                        sx={{ mt: 0.5 }}
                        control={
                          <Switch
                            checked={unit.usage_graph_mode !== UsageGraphMode.HIDDEN}
                            onChange={handleToggleIncludeUsageGraph}
                            disabled={loading}
                            size="small"
                          />
                        }
                        label={
                          <Typography fontSize={13}>
                            {unit.usage_graph_mode !== UsageGraphMode.HIDDEN
                              ? 'Included'
                              : 'Hidden'}
                          </Typography>
                        }
                      />
                    </Stack>
                    <Stack>
                      <Typography fontWeight={'bold'} fontSize={15}>
                        Bill Model
                      </Typography>
                      <Select
                        labelId="demo-simple-select-standard-label"
                        id="demo-simple-select-standard"
                        value={unitModel ?? ''}
                        onChange={(event) => {
                          const next = event.target.value as BillModel;
                          if (next === unitModel) return;
                          setSelectedModel(next);
                          setDialogModel(true);
                        }}
                        variant="standard"
                        renderValue={(value) =>
                          value
                            ? `${
                                value === 'BY_USAGE' || value === 'NON_BILLABLE_UNIT'
                                  ? 'Unit'
                                  : 'Common Area'
                              }: ${BILL_MODEL_LABELS[value as BillModel]}`
                            : ''
                        }
                      >
                        <ListSubheader>Unit</ListSubheader>
                        <MenuItem value={'BY_USAGE'}>By Usage</MenuItem>
                        <MenuItem value={'NON_BILLABLE_UNIT'}>Non Billable</MenuItem>
                        <ListSubheader>Common Area</ListSubheader>
                        <MenuItem value={'SPLIT'}>Split</MenuItem>
                        <MenuItem value={'NON_BILLABLE_COMMON_AREA'}>Non Billable</MenuItem>
                      </Select>
                    </Stack>
                  </Stack>
                </Stack>
                <TabContext value={value}>
                  <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <TabList onChange={handleChange} aria-label="lab API tabs example">
                      {canViewUsage ? (
                        <Tab label="Usage" value="2" />
                      ) : (
                        <Tooltip
                          title="You don't have permission to view usage data. Please contact your account owner."
                          arrow
                        >
                          <span>
                            <Tab label="Usage" value="2" disabled />
                          </span>
                        </Tooltip>
                      )}
                      {stripeConnect && <Tab label="Billing" value="1" />}
                    </TabList>
                  </Box>
                  {stripeConnect && (
                    <TabPanel value="1">
                      {invoices && <BillHistoryTable data={invoices} timeZone={timeZone} />}
                    </TabPanel>
                  )}
                  <TabPanel value="2">
                    {structure && (
                      <DeviceStatusTable
                        devices={unit.devices}
                        refreshData={() => refreshData()}
                        timeZone={structure?.timezone_key || ''}
                        structure={structure}
                        unitInfo={{ unit_id: unit.id, unit_name: unit.name }}
                      />
                    )}
                    <Box position="relative" sx={{ marginTop: 2, marginBottom: 4 }}>
                      <Box>
                        <Box textAlign="center" className="custom-range-picker" marginBottom="15px">
                          <RangePicker
                            format={'MMM D, YYYY'}
                            allowClear={false}
                            maxDate={dayjs(format(endOfMonth(today), 'yyyy-MM-dd'), dateFormat)}
                            onChange={(value, dateString) => handleDateChange(dateString)}
                            value={
                              range && range[0] && range[1]
                                ? [
                                    dayjs(format(range[0], 'yyyy-MM-dd'), dateFormat),
                                    dayjs(format(range[1], 'yyyy-MM-dd'), dateFormat),
                                  ]
                                : undefined
                            }
                            presets={[
                              {
                                label: <span aria-label="Current month">Current month</span>,
                                value: () => [dayjs().startOf('month'), dayjs().endOf('month')],
                              },
                            ]}
                          />
                        </Box>
                        {hasHourlyUsage && (
                          <Box textAlign="center" marginBottom="10px">
                            <ToggleButtonGroup
                              size="small"
                              exclusive
                              value={usageGranularity}
                              onChange={(_e, value) => {
                                if (value !== null) setUsageGranularity(value);
                              }}
                              aria-label="Usage granularity"
                            >
                              <ToggleButton value="daily" aria-label="Daily">
                                Daily
                              </ToggleButton>
                              <ToggleButton value="hourly" aria-label="Hourly">
                                Hourly
                              </ToggleButton>
                            </ToggleButtonGroup>
                          </Box>
                        )}
                        {/* "Combined Usage" header is only meaningful as a contrast label
                            when there's also a Per Meter Breakdown chart below. For
                            single-meter units the word "combined" implies a breakdown
                            that doesn't exist, so we drop the header and just show the
                            total. */}
                        {hasMultipleMeters && (
                          <Typography
                            variant="h6"
                            align="center"
                            color={theme.palette.grey[700]}
                            sx={{ mb: 0.5 }}
                          >
                            Combined Usage
                          </Typography>
                        )}
                        <UsageTotalHeadline
                          meteredVolume={getTotalVolume(graphData || [])}
                          estimatedVolume={getTotalVolume(estimatedGraphData || [])}
                        />
                      </Box>
                      <Box className={classes.chartBody}>
                        {showUsagePermissionMessage ? (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: '400px',
                              width: '100%',
                            }}
                          >
                            <Typography variant="h6" color="text.secondary" align="center">
                              You don't have permission to view usage data.
                              <br />
                              Please contact your account owner.
                            </Typography>
                          </Box>
                        ) : (
                          <>
                            <IconButton
                              size="large"
                              className={classNames(classes.prevNextButton, classes.prevButton)}
                              onClick={handleClickPrev}
                            >
                              <ArrowLeftIcon fontSize="large" />
                            </IconButton>
                            <Box
                              sx={{
                                opacity: isLoadingUsage ? 0.2 : undefined,
                                display: 'flex',
                                flexDirection: 'column',
                              }}
                            >
                              <Chart
                                data={graphData || []}
                                estimated={estimatedGraphData}
                                x={'date'}
                                y={'gallons'}
                                granularity={showHourly ? 'hourly' : 'daily'}
                                timezone={timeZone || undefined}
                                xDomain={
                                  showHourly && range[0] && range[1]
                                    ? [
                                        moment
                                          .tz(
                                            format(range[0], 'yyyy-MM-dd'),
                                            timeZone || moment.tz.guess(),
                                          )
                                          .startOf('day')
                                          .valueOf(),
                                        moment
                                          .tz(
                                            format(range[1], 'yyyy-MM-dd'),
                                            timeZone || moment.tz.guess(),
                                          )
                                          .endOf('day')
                                          .valueOf(),
                                      ]
                                    : undefined
                                }
                                createCSV={() => {
                                  // Guard against clicking export before the SWR fetch resolves —
                                  // otherwise the resulting CSV is just headers / empty.
                                  const dataReady = showHourly ? !!rawHourlyUsage : !!rawUsage;
                                  if (!dataReady) {
                                    toast.info(
                                      'Usage data is still loading. Please wait a moment and try again.',
                                      {
                                        closeButton: true,
                                      },
                                    );
                                    return Promise.reject(new Error('Usage data not loaded yet'));
                                  }
                                  const csvMetadata = {
                                    start: convertTimestampToTimeZone(
                                      getUnixTime(range[0]!),
                                      timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
                                      false,
                                      undefined,
                                      true,
                                    ),
                                    end: convertTimestampToTimeZone(
                                      getUnixTime(range[1]!),
                                      timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
                                      false,
                                      undefined,
                                      true,
                                    ),
                                    timezone:
                                      timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
                                  };
                                  return Promise.resolve(
                                    showHourly
                                      ? generateHourlyUsageCSV(rawHourlyUsage, csvMetadata)
                                      : generateUsageCSV(rawUsage, csvMetadata),
                                  );
                                }}
                                eventType="Unit Usage Exported"
                                eventData={eventData}
                                csvTitle={`${
                                  moveByMonth
                                    ? format(startOfMonth(range[0]!), 'MM_dd_yyyy')
                                    : `${format(range[0]!, 'MM_dd_yyyy')}-${format(
                                        range[1]!,
                                        'MM_dd_yyyy',
                                      )}`
                                }_${structure?.name}_${unit.name}_usage`}
                              />
                            </Box>
                            <IconButton
                              size="large"
                              className={classNames(classes.prevNextButton, classes.nextButton)}
                              onClick={handleClickNext}
                              disabled={!canGoNext}
                            >
                              <ArrowRightIcon fontSize="large" />
                            </IconButton>
                            {isLoadingUsage && (
                              <div className={classes.chartLoader}>
                                <LinearProgress />
                              </div>
                            )}
                          </>
                        )}
                      </Box>
                      {/* Per-meter breakdown chart, only for multi-meter units. Stacked
                          bars (one color per meter) with a legend underneath, sharing the
                          combined chart's date range and granularity. The combined chart
                          above keeps its original behavior; this just adds a second view
                          beneath it so users don't have to export to Excel to see how
                          much each meter contributed. No second export button: the
                          spreadsheet from the combined chart already includes per-meter
                          columns. */}
                      {hasMultipleMeters && !isPermissionDenied && (
                        <Box sx={{ marginTop: '24px' }}>
                          <Typography
                            variant="h6"
                            align="center"
                            color={theme.palette.grey[700]}
                            sx={{ mb: 1 }}
                          >
                            Per Meter Breakdown
                          </Typography>
                          <Box
                            sx={{
                              opacity: isLoadingUsage ? 0.2 : undefined,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                            }}
                          >
                            <Chart
                              data={[]}
                              series={meterSeries}
                              estimated={estimatedGraphData}
                              x={'date'}
                              y={'gallons'}
                              granularity={showHourly ? 'hourly' : 'daily'}
                              timezone={timeZone || undefined}
                              xDomain={
                                showHourly && range[0] && range[1]
                                  ? [
                                      moment
                                        .tz(
                                          format(range[0], 'yyyy-MM-dd'),
                                          timeZone || moment.tz.guess(),
                                        )
                                        .startOf('day')
                                        .valueOf(),
                                      moment
                                        .tz(
                                          format(range[1], 'yyyy-MM-dd'),
                                          timeZone || moment.tz.guess(),
                                        )
                                        .endOf('day')
                                        .valueOf(),
                                    ]
                                  : undefined
                              }
                            />
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </TabPanel>
                  <TabPanel value="3">
                    <AlertsView stripeConnect={true} />
                  </TabPanel>
                </TabContext>
              </div>
            </div>

            <DeleteConfirmDialog
              open={openDeleteConfirm}
              onClose={() => setOpenDeleteConfirm(false)}
              onConfirm={handleClickRemove}
              title="Do you really want to delete this unit?"
              loading={loading}
            />
          </div>
        )}

        <Dialog open={dialogModel} onClose={() => setDialogModel(false)} maxWidth="sm" fullWidth>
          <DialogTitle id="alert-dialog-title">
            Change Bill Model
            <IconButton
              aria-label="close"
              onClick={() => setDialogModel(false)}
              size="small"
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Stack gap={2}>
              {unitModel &&
                selectedModel &&
                (() => {
                  // Stacked From / To with a vertical arrow between them. The
                  // earlier inline form joined the From and To with a dash and
                  // an arrow, which was hard to parse because the two glyphs
                  // looked similar at body-text size. Splitting category and
                  // bill model into separate columns and putting the arrow on
                  // its own line removes the ambiguity entirely.
                  //
                  // Bold whichever side(s) actually differ between From and
                  // To, so the user's eye lands on what's changing instead of
                  // re-reading both rows. Compare on the rendered label text
                  // (not the enum value) so e.g. "Non Billable" -> "Non
                  // Billable" across the Unit/Common Area boundary doesn't
                  // get bolded as if the bill model name changed.
                  const fromCategory =
                    unitModel === 'BY_USAGE' || unitModel === 'NON_BILLABLE_UNIT'
                      ? 'Unit'
                      : 'Common Area';
                  const toCategory =
                    selectedModel === 'BY_USAGE' || selectedModel === 'NON_BILLABLE_UNIT'
                      ? 'Unit'
                      : 'Common Area';
                  const fromModel = BILL_MODEL_LABELS[unitModel];
                  const toModel = BILL_MODEL_LABELS[selectedModel];
                  const categoryChanged = fromCategory !== toCategory;
                  const modelLabelChanged = fromModel !== toModel;
                  const categoryWeight = categoryChanged ? 700 : 400;
                  const modelWeight = modelLabelChanged ? 700 : 400;
                  return (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'auto auto auto',
                        rowGap: 0.5,
                        columnGap: 1.5,
                        alignItems: 'center',
                        bgcolor: 'grey.50',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        px: 2,
                        py: 1.5,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        From
                      </Typography>
                      <Typography variant="body2" fontWeight={categoryWeight}>
                        {fromCategory}
                      </Typography>
                      <Typography variant="body2" fontWeight={modelWeight}>
                        {fromModel}
                      </Typography>

                      <Box />
                      <ArrowDownwardIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      <Box />

                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        To
                      </Typography>
                      <Typography variant="body2" fontWeight={categoryWeight}>
                        {toCategory}
                      </Typography>
                      <Typography variant="body2" fontWeight={modelWeight}>
                        {toModel}
                      </Typography>
                    </Box>
                  );
                })()}

              {/* Legal warning for SPLIT, visually identical to the
                  dedicated popup the create flow shows. Splitting
                  common-area water across residential tenants is
                  prohibited in some jurisdictions, so we surface this any
                  time a unit transitions INTO Split. */}
              {selectedModel === 'SPLIT' && (
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'warning.main',
                    borderRadius: 1,
                    backgroundColor: (t) => `${t.palette.warning.main}0a`,
                    px: 2,
                    py: 1.5,
                  }}
                >
                  <Stack direction="row" alignItems="flex-start" gap={1.5}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        color: 'warning.main',
                        backgroundColor: (t) => `${t.palette.warning.main}1f`,
                        flexShrink: 0,
                      }}
                    >
                      <WarningAmberIcon />
                    </Box>
                    <Stack gap={0.5}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        Split Billing
                      </Typography>
                      <Typography variant="body2">
                        This is <strong>not recommended for residential properties</strong> and can
                        be prohibited by law. Please confirm this is a commercial property where
                        you'd like to split the common water costs among tenants.
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
              )}

              {/* Non-billable note. Tenants and meters stay attached; usage
                  keeps recording for leak detection and tracking. The
                  copy differs slightly between unit and common-area
                  variants since the user mental model is different. */}
              {selectedModel === 'NON_BILLABLE_UNIT' && (
                <Alert severity="info">
                  This unit will not be invoiced for water usage. Tenants and meters stay attached,
                  and usage is still recorded for leak detection. You can switch back to By Usage at
                  any time.
                </Alert>
              )}
              {selectedModel === 'NON_BILLABLE_COMMON_AREA' && (
                <Alert severity="info">
                  This common area will not be invoiced. Usage is still recorded for tracking and
                  leak detection.
                </Alert>
              )}

              {/* Category change note. Fires whenever the unit moves between
                  the Unit category and the Common Area category, in either
                  direction. The icon, label, and how this entry is grouped in
                  property lists all change with the category, so it's worth
                  flagging even when nothing else (billing, tenants, devices)
                  is affected. Within-category changes (e.g. By Usage to Non
                  Billable Unit) don't get this note since the categorization
                  stays the same. */}
              {unitModel &&
                selectedModel &&
                (unitModel === 'BY_USAGE' || unitModel === 'NON_BILLABLE_UNIT') !==
                  (selectedModel === 'BY_USAGE' || selectedModel === 'NON_BILLABLE_UNIT') && (
                  <Alert severity="info">
                    {unitModel === 'BY_USAGE' || unitModel === 'NON_BILLABLE_UNIT'
                      ? 'This is being changed from a Unit to a Common Area.'
                      : 'This is being changed from a Common Area to a Unit.'}{' '}
                    The icon and label will update wherever it appears in property lists.
                  </Alert>
                )}

              {/* Tenant panel. Shown for any transition (not just cross-
                  category) whenever the unit has tenants assigned, so the
                  user always knows who's affected by the change. Severity
                  flips to "warning" only when going Unit -> Common Area,
                  since common areas typically don't carry tenants and we
                  want to surface the unusual case more prominently. The
                  zero-tenant case has its own info row below, only on the
                  Unit -> Common Area transition where the absence of
                  tenants is itself reassuring info; other transitions
                  leave it blank to keep the dialog clean. We always
                  hide while subscribers are still loading (showing
                  "0 tenants" mid-fetch would be misleading). */}
              {unitModel &&
                selectedModel &&
                !subscribersLoading &&
                (() => {
                  const wasUnit = unitModel === 'BY_USAGE' || unitModel === 'NON_BILLABLE_UNIT';
                  const willBeUnit =
                    selectedModel === 'BY_USAGE' || selectedModel === 'NON_BILLABLE_UNIT';
                  const crossesToCommonArea = wasUnit && !willBeUnit;
                  if (unitTenants.length === 0) {
                    if (!crossesToCommonArea) return null;
                    return (
                      <Alert severity="info">
                        No tenants are assigned to this unit, so nothing needs to be moved before
                        changing it to a Common Area.
                      </Alert>
                    );
                  }
                  return (
                    <Alert severity={crossesToCommonArea ? 'warning' : 'info'}>
                      <AlertTitle>
                        {`${unitTenants.length} tenant${
                          unitTenants.length === 1 ? '' : 's'
                        } assigned to this unit`}
                      </AlertTitle>
                      <Stack gap={1}>
                        {/* Tenant names. first_name / last_name are typed
                            as required but can be saved blank, so fall
                            back to the (always-required) email so we
                            never render an empty entry. */}
                        <Typography variant="body2">
                          {unitTenants.slice(0, 5).map(tenantDisplayName).join(', ')}
                          {unitTenants.length > 5 && ` and ${unitTenants.length - 5} more`}
                        </Typography>
                        {crossesToCommonArea && (
                          <Typography variant="body2">
                            Common areas typically don't have tenants. Consider removing them on the
                            Tenants page first.
                          </Typography>
                        )}
                      </Stack>
                    </Alert>
                  );
                })()}

              {/* Past invoices are unaffected; the change applies to
                  future bills only. State this once for any non-trivial
                  transition so users aren't surprised. */}
              <Typography variant="body2" color="text.secondary">
                Past invoices are unchanged. The new bill model takes effect on the next invoice
                generated for this property.
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <SWLoadingButtonOutlined onClick={() => setDialogModel(false)}>
              Cancel
            </SWLoadingButtonOutlined>
            <SWLoadingButton
              onClick={handleUpdateUnitFlags}
              autoFocus
              loading={loading}
              color={selectedModel === 'SPLIT' ? 'warning' : 'primary'}
            >
              {selectedModel === 'SPLIT' ? 'I understand, continue' : 'Confirm'}
            </SWLoadingButton>
          </DialogActions>
        </Dialog>
      </div>
    </ErrorBoundary>
  );
};
